import type { DistrictEntry } from './election-config'

/**
 * Pure identity / location helpers shared by the API (dedupe, policy) and the web app (previews).
 */

/** Lower-case + trim. We do NOT strip dots / plus-tags: they are distinct mailboxes at most providers. */
export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase()
}

/**
 * Canonical national id number for per-organization dedupe: upper case, only A–Z / 0–9
 * (drops spaces, dashes, dots, slashes), Unicode-normalized so `ñ`-like input cannot sneak duplicates.
 * The backend stores sha256(`${countryIso3}:${normalized}`) for the unique index.
 */
export function normalizeNationalId(id: string): string {
  return id
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
}

/** Parse `YYYY-MM-DD` (or a Date) into UTC y/m/d parts. Returns null if invalid. */
function ymd(date: string | Date): { y: number; m: number; d: number } | null {
  if (date instanceof Date) {
    if (Number.isNaN(date.getTime())) return null
    return { y: date.getUTCFullYear(), m: date.getUTCMonth() + 1, d: date.getUTCDate() }
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(date.trim())
  if (!match) return null
  const [y, m, d] = [Number(match[1]), Number(match[2]), Number(match[3])]
  const probe = new Date(Date.UTC(y, m - 1, d))
  if (probe.getUTCFullYear() !== y || probe.getUTCMonth() !== m - 1 || probe.getUTCDate() !== d) return null
  return { y, m, d }
}

/**
 * Completed years of age at `at` (UTC calendar). A person born on Feb 29 turns a year older on
 * Mar 1 in non-leap years. Returns null for unparsable input or a birth date after `at`.
 */
export function ageAt(dob: string | Date, at: string | Date): number | null {
  const b = ymd(dob)
  const a = ymd(at)
  if (!b || !a) return null
  let age = a.y - b.y
  if (a.m < b.m || (a.m === b.m && a.d < b.d)) age -= 1
  return age < 0 ? null : age
}

/** Locality matching key: case/accents/punctuation-insensitive, collapsed whitespace. */
export function normalizeLocality(locality: string): string {
  return locality
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

export type DistrictResolution =
  | { status: 'not_applicable'; district: null }
  | { status: 'resolved'; district: string; locality: string }
  | { status: 'unresolved'; district: null }

/**
 * Map a self-declared locality to its district using the organization's districting list.
 * - empty list ⇒ not_applicable
 * - exact normalized match on a single district ⇒ resolved (returns the list's canonical locality)
 * - no match, or a locality that maps to several different districts ⇒ unresolved (organizer review)
 */
export function resolveDistrict(locality: string, districts: readonly DistrictEntry[]): DistrictResolution {
  if (districts.length === 0) return { status: 'not_applicable', district: null }
  const key = normalizeLocality(locality)
  if (!key) return { status: 'unresolved', district: null }
  const matches = districts.filter((d) => normalizeLocality(d.locality) === key)
  const distinct = new Set(matches.map((m) => m.district))
  if (matches.length === 0 || distinct.size !== 1) return { status: 'unresolved', district: null }
  const first = matches[0]!
  return { status: 'resolved', district: first.district, locality: first.locality }
}

/**
 * Parse a districting CSV (`locality,district` per line; optional header; `"quoted, values"` allowed).
 * Returns entries and per-line errors (1-based line numbers).
 */
export function parseDistrictsCsv(csv: string): {
  entries: DistrictEntry[]
  errors: { line: number; message: string }[]
} {
  const entries: DistrictEntry[] = []
  const errors: { line: number; message: string }[] = []
  const lines = csv.split(/\r?\n/)
  lines.forEach((raw, i) => {
    const line = raw.trim()
    if (!line || line.startsWith('#')) return
    const cells = splitCsvLine(line)
    if (i === 0 && cells[0]?.toLowerCase() === 'locality') return
    const [locality, district] = cells.map((c) => c.trim())
    if (cells.length < 2 || !locality || !district) {
      errors.push({ line: i + 1, message: 'Expected "locality,district"' })
      return
    }
    entries.push({ locality, district })
  })
  return { entries, errors }
}

function splitCsvLine(line: string): string[] {
  const out: string[] = []
  let cur = ''
  let quoted = false
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (quoted) {
      if (ch === '"' && line[i + 1] === '"') {
        cur += '"'
        i++
      } else if (ch === '"') quoted = false
      else cur += ch
    } else if (ch === '"') quoted = true
    else if (ch === ',' || ch === ';') {
      out.push(cur)
      cur = ''
    } else cur += ch
  }
  out.push(cur)
  return out
}

/** Great-circle distance in km (haversine). */
export function distanceKm(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const R = 6371.0088
  const rad = (x: number) => (x * Math.PI) / 180
  const dLat = rad(b.lat - a.lat)
  const dLng = rad(b.lng - a.lng)
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)))
}

/** Split a full name into first / last for Didit `expected_details` (first token vs rest). */
export function splitFullName(fullName: string): { firstName: string; lastName: string } {
  const parts = fullName.trim().split(/\s+/).filter(Boolean)
  return { firstName: parts[0] ?? '', lastName: parts.slice(1).join(' ') }
}
